import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Avatar,
  Container,
  Divider,
  Stack,
} from "@mui/material";
import {
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  AttachMoney as MoneyIcon,
  VerifiedUser as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactElement;
  gradient: string;
  subtitle?: string;
  trend?: {
    value: number;
    label: string;
  };
}

interface AnalyticsData {
  users: {
    total: number;
    verified: number;
    unverified: number;
    recent: number;
  };
  challans: {
    total: number;
    paid: number;
    unpaid: number;
    recent: number;
  };
  scholarships: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    recent: number;
  };
  revenue: {
    total: number;
    pending: number;
    collected: number;
  };
}

function StatCard({
  title,
  value,
  icon,
  gradient,
  subtitle,
  trend,
}: StatCardProps) {
  return (
    <Card
      sx={{
        height: "100%",
        background: gradient,
        color: "white",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ position: "relative", zIndex: 2 }}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={2}
        >
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              width: 56,
              height: 56,
              backdropFilter: "blur(10px)",
            }}
          >
            {icon}
          </Avatar>
          {trend && (
            <Box textAlign="right">
              <Box display="flex" alignItems="center">
                <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                <Typography variant="body2" fontWeight="bold">
                  +{trend.value}%
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {trend.label}
              </Typography>
            </Box>
          )}
        </Box>

        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {value}
        </Typography>

        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          {title}
        </Typography>

        {subtitle && (
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>

      {/* Decorative background elements */}
      <Box
        sx={{
          position: "absolute",
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -30,
          left: -30,
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
          zIndex: 1,
        }}
      />
    </Card>
  );
}

function ProgressCard({
  title,
  current,
  total,
  color,
  icon,
}: {
  title: string;
  current: number;
  total: number;
  color: string;
  icon: React.ReactElement;
}) {
  const percentage = total > 0 ? (current / total) * 100 : 0;

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: `${color}.light`, mr: 2 }}>{icon}</Avatar>
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" color={`${color}.main`} fontWeight="bold">
              {current.toLocaleString()}
            </Typography>
          </Box>
        </Box>

        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: `${color}.light`,
            "& .MuiLinearProgress-bar": {
              bgcolor: `${color}.main`,
            },
          }}
        />

        <Box display="flex" justifyContent="space-between" mt={1}>
          <Typography variant="body2" color="text.secondary">
            {percentage.toFixed(1)}% of {total.toLocaleString()}
          </Typography>
          <Typography
            variant="body2"
            color={`${color}.main`}
            fontWeight="medium"
          >
            {(total - current).toLocaleString()} remaining
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const user = localStorage.getItem("user");

  const isAdmin = user ? JSON.parse(user).role === "admin" : false;

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get("/analytics/dashboard");
      setAnalytics(response.data.data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2 }} color="text.secondary">
            Loading dashboard data...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const verificationRate = analytics?.users?.total
    ? ((analytics.users.verified / analytics.users.total) * 100).toFixed(1)
    : "0";

  const paymentRate = analytics?.challans?.total
    ? ((analytics.challans.paid / analytics.challans.total) * 100).toFixed(1)
    : "0";

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>

      {isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Note:</strong> Revenue information is not displayed for admin
          users as per system configuration.
        </Alert>
      )}

      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2 }} color="text.secondary">
            Loading dashboard data...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Header Section */}
          <Box mb={4}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Dashboard Overview
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Welcome to your Hunarmand Punjab management system. Here's what's
              happening today.
            </Typography>
          </Box>

          {/* Main Stats Grid */}
          <Box display="flex" flexWrap="wrap" gap={3} mb={4}>
            <Box flex="1 1 250px" minWidth="250px">
              <StatCard
                title="Total Users"
                value={analytics?.users?.total?.toLocaleString() || "0"}
                icon={<PeopleIcon />}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                subtitle={`${verificationRate}% verified`}
                trend={{ value: 12, label: "This month" }}
              />
            </Box>

            <Box flex="1 1 250px" minWidth="250px">
              <StatCard
                title="Verified Users"
                value={isAdmin ? "N/A" : analytics?.users?.verified?.toLocaleString() || "0"}
                icon={<VerifiedIcon />}
                gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                subtitle="Active accounts"
                trend={{ value: 8, label: "This week" }}
              />
            </Box>

            <Box flex="1 1 250px" minWidth="250px">
              <StatCard
                title="Total Challans"
                value={isAdmin ? "N/A" : analytics?.challans?.total?.toLocaleString() || "0"}
                icon={<ReceiptIcon />}
                gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                subtitle={`${paymentRate}% paid`}
                trend={{ value: 15, label: "This month" }}
              />
            </Box>

            <Box flex="1 1 250px" minWidth="250px">
              <StatCard
                title="Total Revenue"
                value={
                  isAdmin
                    ? "N/A"
                    : formatCurrency(analytics?.revenue?.total || 0)
                }
                icon={<MoneyIcon />}
                gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
                subtitle="Collected amount"
                trend={{ value: 25, label: "This month" }}
              />
            </Box>

            <Box flex="1 1 250px" minWidth="250px">
              <StatCard
                title="Total Scholarships"
                value={isAdmin ? "N/A" : analytics?.scholarships?.total?.toLocaleString() || "0"}
                icon={<SchoolIcon />}
                gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                subtitle={`${analytics?.scholarships?.pending || 0} pending`}
                trend={{ value: 5, label: "This month" }}
              />
            </Box>

            <Box flex="1 1 250px" minWidth="250px">
              <StatCard
                title="Approved Scholarships"
                value={
                  isAdmin ? "N/A" : analytics?.scholarships?.approved?.toLocaleString() || "0"
                }
                icon={<CheckCircleIcon />}
                gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                subtitle="Success rate"
                trend={{ value: 18, label: "This month" }}
              />
            </Box>
          </Box>

          {/* Detailed Analytics */}

          {!isAdmin && (
            <Box display="flex" flexWrap="wrap" gap={3} mb={4}>
              <Box flex="1 1 400px" minWidth="400px">
                <ProgressCard
                  title="User Verification Progress"
                  current={analytics?.users?.verified || 0}
                  total={analytics?.users?.total || 0}
                  color="success"
                  icon={<VerifiedIcon />}
                />
              </Box>

              <Box flex="1 1 400px" minWidth="400px">
                <ProgressCard
                  title="Payment Collection Progress"
                  current={analytics?.challans?.paid || 0}
                  total={analytics?.challans?.total || 0}
                  color="info"
                  icon={<CheckCircleIcon />}
                />
              </Box>
            </Box>
          )}

          {/* Summary Cards */}



          {!isAdmin && (
          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box flex="1 1 300px" minWidth="300px">
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: "primary.light", mr: 2 }}>
                      <AssessmentIcon />
                    </Avatar>
                    <Typography variant="h6">User Analytics</Typography>
                  </Box>

                  <Stack spacing={2}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center">
                        <CheckCircleIcon
                          color="success"
                          sx={{ mr: 1, fontSize: 20 }}
                        />
                        <Typography>Verified Users</Typography>
                      </Box>
                      <Chip
                        label={analytics?.users?.verified?.toLocaleString()}
                        color="success"
                        size="small"
                      />
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center">
                        <CancelIcon
                          color="warning"
                          sx={{ mr: 1, fontSize: 20 }}
                        />
                        <Typography>Unverified Users</Typography>
                      </Box>
                      <Chip
                        label={analytics?.users?.unverified?.toLocaleString()}
                        color="warning"
                        size="small"
                      />
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center">
                        <PersonAddIcon
                          color="info"
                          sx={{ mr: 1, fontSize: 20 }}
                        />
                        <Typography>Recent Signups</Typography>
                      </Box>
                      <Chip
                        label={analytics?.users?.recent?.toLocaleString()}
                        color="info"
                        size="small"
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            <Box flex="1 1 300px" minWidth="300px">
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: "secondary.light", mr: 2 }}>
                      <ReceiptIcon />
                    </Avatar>
                    <Typography variant="h6">Challan Analytics</Typography>
                  </Box>

                  <Stack spacing={2}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center">
                        <CheckCircleIcon
                          color="success"
                          sx={{ mr: 1, fontSize: 20 }}
                        />
                        <Typography>Paid Challans</Typography>
                      </Box>
                      <Chip
                        label={analytics?.challans?.paid?.toLocaleString()}
                        color="success"
                        size="small"
                      />
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center">
                        <CancelIcon
                          color="error"
                          sx={{ mr: 1, fontSize: 20 }}
                        />
                        <Typography>Unpaid Challans</Typography>
                      </Box>
                      <Chip
                        label={analytics?.challans?.unpaid?.toLocaleString()}
                        color="error"
                        size="small"
                      />
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center">
                        <ScheduleIcon
                          color="info"
                          sx={{ mr: 1, fontSize: 20 }}
                        />
                        <Typography>Recent Challans</Typography>
                      </Box>
                      <Chip
                        label={analytics?.challans?.recent?.toLocaleString()}
                        color="info"
                        size="small"
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            <Box flex="1 1 300px" minWidth="300px">
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: "success.light", mr: 2 }}>
                      <MoneyIcon />
                    </Avatar>
                    <Typography variant="h6">Revenue Analytics</Typography>
                  </Box>

                  {isAdmin ? (
                    <Box textAlign="center" py={3}>
                      <Typography variant="body2" color="text.secondary">
                        Revenue information is not available for admin users
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Total Collected
                        </Typography>
                        <Typography
                          variant="h5"
                          color="success.main"
                          fontWeight="bold"
                        >
                          {formatCurrency(analytics?.revenue?.collected || 0)}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Pending Amount
                        </Typography>
                        <Typography
                          variant="h6"
                          color="warning.main"
                          fontWeight="medium"
                        >
                          {formatCurrency(analytics?.revenue?.pending || 0)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Collection Rate
                        </Typography>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="h6"
                            color="primary.main"
                            fontWeight="bold"
                          >
                            {paymentRate}%
                          </Typography>
                          <TrendingUpIcon color="success" sx={{ ml: 1 }} />
                        </Box>
                      </Box>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>

          )}
        </>
      )}
    </Container>
  );
}

export default Dashboard;
